import { useState } from 'react';
import { Card, CardHeader, CardBody, Icon, Button } from '@repo/ui';
import { numberUtil } from '@xtnd/web/shared/utils';
import { DataTable, DataTableColumn } from '@xtnd/web/shared/components';
import { SummaryCardsGrid } from './SummaryCardsGrid';

export interface AccountTransactionSectionProps {
  accountId: string;
  accountName: string;
  transactions: any[];
  summary: {
    totalIn: number;
    totalOut: number;
    netAmount: number;
    transactionCount: number;
  };
  columns: DataTableColumn<any>[];
  isExpandedByDefault?: boolean;
  showAccountLink?: boolean;
}

/**
 * AccountTransactionSection - Displays transactions grouped by account
 * with collapsible functionality and account-level summaries
 */
export function AccountTransactionSection({
  accountId,
  accountName,
  transactions,
  summary,
  columns,
  isExpandedByDefault = true,
  showAccountLink = true,
}: AccountTransactionSectionProps) {
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);

  const accountUrl = accountId && accountId !== 'unknown'
    ? `https://app.clio.com/nc/#/bank_accounts/${accountId}/transactions`
    : null;

  const summaryCards = [
    {
      title: 'Transactions',
      value: numberUtil.formatNumber(summary.transactionCount),
      subtitle: '',
      icon: 'HiClipboardList',
      color: 'text-xtnd-blue dark:text-xtnd-blue-400',
      bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
      variant: 'default' as const,
      padding: 'sm' as const,
      valueSize: 'sm' as const,
    },
    {
      title: 'Total In',
      value: numberUtil.formatCurrency(summary.totalIn),
      subtitle: '',
      icon: 'HiArrowUp',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
      variant: 'default' as const,
      padding: 'sm' as const,
      valueSize: 'sm' as const,
    },
    {
      title: 'Total Out',
      value: numberUtil.formatCurrency(summary.totalOut),
      subtitle: '',
      icon: 'HiArrowDown',
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
      variant: 'default' as const,
      padding: 'sm' as const,
      valueSize: 'sm' as const,
    },
    {
      title: 'Net Amount',
      value: numberUtil.formatCurrency(summary.netAmount),
      subtitle: '',
      icon: summary.netAmount >= 0 ? 'HiTrendingUp' : 'HiTrendingDown',
      color: summary.netAmount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
      bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
      variant: 'default' as const,
      padding: 'sm' as const,
      valueSize: 'sm' as const,
    },
  ];

  return (
    <Card variant="outlined" className="overflow-hidden">
      <CardHeader className="bg-xtnd-light dark:bg-xtnd-dark-800 border-b border-xtnd-blue dark:border-xtnd-blue-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-xtnd-blue/10 dark:hover:bg-xtnd-blue-900/20"
              aria-label={isExpanded ? 'Collapse account section' : 'Expand account section'}
            >
              <Icon
                name={isExpanded ? 'HiChevronDown' : 'HiChevronRight'}
                className="h-5 w-5 text-xtnd-dark dark:text-xtnd-light transition-transform"
              />
            </Button>

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                  {accountName}
                </h3>
                {showAccountLink && accountUrl && (
                  <a
                    href={accountUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xtnd-blue dark:text-xtnd-blue-400 hover:underline inline-flex items-center gap-1"
                    title="View account in Clio"
                  >
                    <Icon name="HiExternalLink" className="h-4 w-4" />
                  </a>
                )}
              </div>
              <div className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-300">
                {summary.transactionCount} {summary.transactionCount === 1 ? 'transaction' : 'transactions'}
                {' • '}
                Net: <span className={summary.netAmount >= 0 ? 'text-green-600 dark:text-green-400 font-medium' : 'text-red-600 dark:text-red-400 font-medium'}>
                  {numberUtil.formatCurrency(summary.netAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Summary Stats */}
          <div className="hidden lg:flex items-center gap-6 text-sm">
            <div className="text-right">
              <div className="text-xtnd-dark-600 dark:text-xtnd-dark-400">Total In</div>
              <div className="font-semibold text-green-600 dark:text-green-400">
                {numberUtil.formatCurrency(summary.totalIn)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xtnd-dark-600 dark:text-xtnd-dark-400">Total Out</div>
              <div className="font-semibold text-red-600 dark:text-red-400">
                {numberUtil.formatCurrency(summary.totalOut)}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardBody className="p-0">
          {/* Summary Cards for Mobile/Tablet */}
          <div className="lg:hidden p-4 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600">
            <SummaryCardsGrid
              cards={summaryCards}
              columns={{ mobile: 2, tablet: 4, desktop: 4 }}
              gap="sm"
            />
          </div>

          {/* Transactions Table */}
          <div className="p-4">
            <DataTable
              columns={columns}
              data={{ data: transactions }}
              isLoading={false}
              error={null}
              emptyState={{
                icon: 'HiClipboardList',
                title: 'No transactions',
                description: `No transactions found for ${accountName}`,
              }}
              hidePagination={true}
              tableContainerClassName="overflow-x-auto"
              tableClassName="min-w-full divide-y divide-xtnd-dark-100 dark:divide-xtnd-dark-600"
              compact={true}
            />
          </div>
        </CardBody>
      )}
    </Card>
  );
}



